import { NodeIds, InputIds } from '../misc';
import { MainClassInput, ClassInput, SubClassInput, CountryGroupsInput, CountriesInput } from './SharedInputs';
import {
    BusinessIdInput,
    ExpiringPeriodStartInput,
    TitleInput,
    CreateProgramInput,
    TypeOfParticipationInput,
    BusinessTypeInput,
    UnderwritingYearInput,
    PeriodStartInput,
    PeriodEndInput,
    LifecycleInput,
    CurrencyInput,
    CedentInput,
    InsuredInput,
    BrokerInput,
    ReinsurerInput,
    ReportUnitsInput
} from './GeneralInformationNodeInputs';
import {
    CoverInput,
    ExcessInput,
    LiabilityInput,
    MainLimitInput,
    TotalSumInsuredInput,
    LimitTypeInput,
    LimitValueInput
} from './LimitsNodeInputs';
import {
    EstimatedPremiumIncomeInput,
    MinimalPremiumInput,
    DepositPremiumInput,
    SubjectPremiumIncomeInput,
    PremiumRateInput,
    PremiumTypeInput,
    TotalGrossPremiumInput,
    AnnualPremiumInput,
    FlatPremiumInput
} from './PremiumsNodeInputs';
import {
    CommissionInput,
    BrokerageInput,
    TaxOnPremiumInput,
    DeductionTypeInput,
    DeductionValueInput
} from './DeductionsNodeInputs';
import { ShareOfferInput, ShareWrittenInput, ShareSignedInput, CessionPercentInput } from './ShareNodeInputs';
import { ClaimBasisInput, ClaimAdviceLimitInput } from './ClaimsNodeInputs';
import {
    PremiumEntryCalculationMethodInput,
    PremiumEntryPercentInput,
    PremiumEntryPercentOfInput,
    PremiumWithdrawalCalculationMethodInput,
    PremiumWithdrawalPercentInput,
    PremiumWithdrawalPercentOfInput,
    LossEntryCalculationMethodInput,
    LossEntryPercentInput,
    LossWithdrawalCalculationMethodInput,
    LossWithdrawalPercentInput
} from './PortfolioConditionsNodeInputs';
import { CessionBasisInput, CashClaimLimitInput } from './AdminConditionsNodeInputs';
import {
    CancellationTermsInput,
    NOCPeriodInput,
    PremiumPaymentWarrantyInput,
    PPWPeriodInput,
    InclusionClausesInput,
    ExclusionClausesInput
} from './OtherConditionsNodeInputs';
import {
    InstalmentsCountInput,
    InstalmentTypeInput,
    FirstInstalmentCountInput,
    FirstInstalmentPeriodInput,
    SubsequentInstalmentCountInput,
    SubsequentInstalmentPeriodInput
} from './InstalmentsNodeInputs';
import {
    AdjustmentsCountInput,
    FirstPeriodCountInput,
    FirstPeriodTimeInput,
    SubsequentAdjustmentsPeriodCountInput,
    SubsequentAdjustmentsPeriodTimeInput,
    SubmissionWithinCountInput,
    SubmissionWithinPeriodInput,
    PaymentWithinCountInput,
    PaymentWithinPeriodInput
} from './AdjustmentsNodeInputs';
import {
    CurrencyTypeInput,
    CalculationRuleInput,
    ReinstatementsTypeInput,
    CountOfReinstatementsInput,
    PaymentRuleInput,
    AmountPercentInput,
    TimePercentInput,
    BrokerageTypeInput,
    BrokeragePercentInput
} from './ReinstatementsNodeInputs';

type InputComponentRegistryType = {
    [NodeId in keyof typeof InputIds]: {
        [InputId in keyof (typeof InputIds)[NodeId]]: React.FunctionComponent;
    };
};

const InputComponentRegistry: InputComponentRegistryType = {
    [NodeIds.GeneralInformation]: {
        [InputIds.GeneralInformation.BusinessId]: BusinessIdInput,
        [InputIds.GeneralInformation.ExpiringPeriodStart]: ExpiringPeriodStartInput,
        [InputIds.GeneralInformation.MainClass]: MainClassInput,
        [InputIds.GeneralInformation.Class]: ClassInput,
        [InputIds.GeneralInformation.SubClass]: SubClassInput,
        [InputIds.GeneralInformation.CountryGroups]: CountryGroupsInput,
        [InputIds.GeneralInformation.Countries]: CountriesInput,
        [InputIds.GeneralInformation.Title]: TitleInput,
        [InputIds.GeneralInformation.CreateProgram]: CreateProgramInput,
        [InputIds.GeneralInformation.TypeOfParticipation]: TypeOfParticipationInput,
        [InputIds.GeneralInformation.BusinessType]: BusinessTypeInput,
        [InputIds.GeneralInformation.UnderwritingYear]: UnderwritingYearInput,
        [InputIds.GeneralInformation.PeriodStart]: PeriodStartInput,
        [InputIds.GeneralInformation.PeriodEnd]: PeriodEndInput,
        [InputIds.GeneralInformation.Lifecycle]: LifecycleInput,
        [InputIds.GeneralInformation.Currency]: CurrencyInput,
        [InputIds.GeneralInformation.Cedent]: CedentInput,
        [InputIds.GeneralInformation.Insured]: InsuredInput,
        [InputIds.GeneralInformation.Broker]: BrokerInput,
        [InputIds.GeneralInformation.Reinsurer]: ReinsurerInput,
        [InputIds.GeneralInformation.ReportUnits]: ReportUnitsInput
    },
    [NodeIds.Classifications]: {
        [InputIds.Classifications.MainClass]: MainClassInput,
        [InputIds.Classifications.Class]: ClassInput,
        [InputIds.Classifications.SubClass]: SubClassInput
    },
    [NodeIds.Countries]: {
        [InputIds.Countries.CountryGroups]: CountryGroupsInput,
        [InputIds.Countries.Countries]: CountriesInput
    },
    [NodeIds.Limits]: {
        [InputIds.Limits.Cover]: CoverInput,
        [InputIds.Limits.Excess]: ExcessInput,
        [InputIds.Limits.Liability]: LiabilityInput,
        [InputIds.Limits.MainLimit]: MainLimitInput,
        [InputIds.Limits.TotalSumInsured]: TotalSumInsuredInput,
        [InputIds.Limits.LimitType]: LimitTypeInput,
        [InputIds.Limits.LimitValue]: LimitValueInput
    },
    [NodeIds.Premiums]: {
        [InputIds.Premiums.PremiumRate]: PremiumRateInput,
        [InputIds.Premiums.PremiumType]: PremiumTypeInput,
        [InputIds.Premiums.EstimatedPremiumIncome]: EstimatedPremiumIncomeInput,
        [InputIds.Premiums.MinimalPremium]: MinimalPremiumInput,
        [InputIds.Premiums.DepositPremium]: DepositPremiumInput,
        [InputIds.Premiums.SubjectPremiumIncome]: SubjectPremiumIncomeInput,
        [InputIds.Premiums.TotalGrossPremium]: TotalGrossPremiumInput,
        [InputIds.Premiums.FlatPremium]: FlatPremiumInput,
        [InputIds.Premiums.AnnualPremium]: AnnualPremiumInput
    },
    [NodeIds.Deductions]: {
        [InputIds.Deductions.Commission]: CommissionInput,
        [InputIds.Deductions.Brokerage]: BrokerageInput,
        [InputIds.Deductions.TaxOnPremium]: TaxOnPremiumInput,
        [InputIds.Deductions.DeductionType]: DeductionTypeInput,
        [InputIds.Deductions.DeductionValue]: DeductionValueInput
    },
    [NodeIds.Share]: {
        [InputIds.Share.ShareOffer]: ShareOfferInput,
        [InputIds.Share.ShareWritten]: ShareWrittenInput,
        [InputIds.Share.ShareSigned]: ShareSignedInput,
        [InputIds.Share.CessionPercent]: CessionPercentInput
    },
    [NodeIds.Claims]: {
        [InputIds.Claims.ClaimBasis]: ClaimBasisInput,
        [InputIds.Claims.ClaimAdviceLimit]: ClaimAdviceLimitInput
    },
    [NodeIds.PortfolioConditions]: {
        [InputIds.PortfolioConditions.PremiumEntryCalculationMethod]: PremiumEntryCalculationMethodInput,
        [InputIds.PortfolioConditions.PremiumEntryPercent]: PremiumEntryPercentInput,
        [InputIds.PortfolioConditions.PremiumEntryPercentOf]: PremiumEntryPercentOfInput,
        [InputIds.PortfolioConditions.PremiumWithdrawalCalculationMethod]: PremiumWithdrawalCalculationMethodInput,
        [InputIds.PortfolioConditions.PremiumWithdrawalPercent]: PremiumWithdrawalPercentInput,
        [InputIds.PortfolioConditions.PremiumWithdrawalPercentOf]: PremiumWithdrawalPercentOfInput,
        [InputIds.PortfolioConditions.LossEntryCalculationMethod]: LossEntryCalculationMethodInput,
        [InputIds.PortfolioConditions.LossEntryPercent]: LossEntryPercentInput,
        [InputIds.PortfolioConditions.LossWithdrawalCalculationMethod]: LossWithdrawalCalculationMethodInput,
        [InputIds.PortfolioConditions.LossWithdrawalPercent]: LossWithdrawalPercentInput
    },
    [NodeIds.AdminConditions]: {
        [InputIds.AdminConditions.CessionBasis]: CessionBasisInput,
        [InputIds.AdminConditions.CashClaimLimit]: CashClaimLimitInput
    },
    [NodeIds.OtherConditions]: {
        [InputIds.OtherConditions.CancellationTerms]: CancellationTermsInput,
        [InputIds.OtherConditions.NOCPeriod]: NOCPeriodInput,
        [InputIds.OtherConditions.PremiumPaymentWarranty]: PremiumPaymentWarrantyInput,
        [InputIds.OtherConditions.PPWPeriod]: PPWPeriodInput,
        [InputIds.OtherConditions.InclusionClauses]: InclusionClausesInput,
        [InputIds.OtherConditions.ExclusionClauses]: ExclusionClausesInput
    },
    [NodeIds.Instalments]: {
        [InputIds.Instalments.InstalmentsCount]: InstalmentsCountInput,
        [InputIds.Instalments.FirstInstalmentCount]: FirstInstalmentCountInput,
        [InputIds.Instalments.FirstInstalmentPeriod]: FirstInstalmentPeriodInput,
        [InputIds.Instalments.InstalmentType]: InstalmentTypeInput,
        [InputIds.Instalments.SubsequentInstalmentCount]: SubsequentInstalmentCountInput,
        [InputIds.Instalments.SubsequentInstalmentPeriod]: SubsequentInstalmentPeriodInput
    },
    [NodeIds.Adjustments]: {
        [InputIds.Adjustments.AdjustmentsCount]: AdjustmentsCountInput,
        [InputIds.Adjustments.FirstPeriodCount]: FirstPeriodCountInput,
        [InputIds.Adjustments.FirstPeriodTime]: FirstPeriodTimeInput,
        [InputIds.Adjustments.SubsequentAdjustmentsPeriodCount]: SubsequentAdjustmentsPeriodCountInput,
        [InputIds.Adjustments.SubsequentAdjustmentsPeriodTime]: SubsequentAdjustmentsPeriodTimeInput,
        [InputIds.Adjustments.SubmissionWithinCount]: SubmissionWithinCountInput,
        [InputIds.Adjustments.SubmissionWithinPeriod]: SubmissionWithinPeriodInput,
        [InputIds.Adjustments.PaymentWithinCount]: PaymentWithinCountInput,
        [InputIds.Adjustments.PaymentWithinPeriod]: PaymentWithinPeriodInput
    },
    [NodeIds.Reinstatements]: {
        [InputIds.Reinstatements.CurrencyType]: CurrencyTypeInput,
        [InputIds.Reinstatements.CalculationRule]: CalculationRuleInput,
        [InputIds.Reinstatements.ReinstatementsType]: ReinstatementsTypeInput,
        [InputIds.Reinstatements.CountOfReinstatements]: CountOfReinstatementsInput,
        [InputIds.Reinstatements.PaymentRule]: PaymentRuleInput,
        [InputIds.Reinstatements.AmountPercent]: AmountPercentInput,
        [InputIds.Reinstatements.TimePercent]: TimePercentInput,
        [InputIds.Reinstatements.BrokerageType]: BrokerageTypeInput,
        [InputIds.Reinstatements.BrokeragePercent]: BrokeragePercentInput
    }
};

export default InputComponentRegistry;
